import { Injectable } from '@angular/core'
import * as FileSaver from 'file-saver'
import { Observable, Subject } from 'rxjs'
import * as XLSX from 'xlsx'
import * as moment_ from 'moment'
import { ImportInventoryError } from '../models/Inventory'

const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8'
const EXCEL_EXTENSION = '.xlsx'

@Injectable()
export class ExcelService {
	constructor() {}
	public exportAsExcelFile(json: Array<any>, excelFileName: string, indexes?: any): void {
		const jsonKeys = Object.keys(json[0])

		const objectMaxLength = []
		for (let i = 0; i < json.length; i++) {
		  const value = json[i]
		  for (let j = 0; j < jsonKeys.length; j++) {
			if (typeof value[jsonKeys[j]] == 'number') {
			  objectMaxLength[j] = 10
			} else {

			  const l = value[jsonKeys[j]] ? value[jsonKeys[j]].length : 0

			  objectMaxLength[j] =
				objectMaxLength[j] >= l
				  ? objectMaxLength[j]
				  : l
			}
		  }

		  const key = jsonKeys
		  for (let j = 0; j < key.length; j++) {
			objectMaxLength[j] =
			  objectMaxLength[j] >= key[j].length
				? objectMaxLength[j]
				: key[j].length
		  }
		}

		const wscols = objectMaxLength.map(w => { return { width: w + 5} })

		const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(json)
		worksheet['!cols'] = wscols

		//HYPERLINK

		for (const column in worksheet) {
			const isPhoto = /https/
		 const valueColumn = worksheet[column].v


		 if (isPhoto.test(valueColumn)) {
								worksheet[column].l = {Target: worksheet[column].v}
						}
		}


		if (indexes) {
			const merge = XLSX.utils.decode_range(indexes.mergeFirstString)
			const merge2 = XLSX.utils.decode_range(indexes.mergeSecondString)
			if (!worksheet['!merges']) worksheet['!merges'] = []
			worksheet['!merges'].push(merge, merge2)
		}
		const workbook: XLSX.WorkBook = { Sheets: { data: worksheet }, SheetNames: ['data'] }
		const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
		this.saveAsExcelFile(excelBuffer, excelFileName)
	}

	/**
	 * Realiza a leitura de um arquivo Excel, valida se os registros da planilha contem todos os atributos do modelo desejado
	 * e retorna um Array de objetos.
	 *
	 * @param {File} file - Árquivo Excel contendo os registros.
	 * @param {T} model - Classe modelo contendo os atributos obrigatórios em cada registro do Excel.
	 * @returns {Array<Object>} - Retorna um Array de objetos, montados a partir dos registros do Arquivo fornecido.
	 */
	public readFileXlsxAndReturnArrayObjects<T>(file: File, model: new () => T): Observable<Array<T>> {
		const reader: FileReader = new FileReader()
		const subject = new Subject<Array<T>>()

		// Valida se o arquivo passado é um excel válido
		const validFile: boolean = this.isValidXlsxFile(file)

		if (!validFile) {
			subject.error('O arquivo não é um excel válido')
		}

		reader.onload = (e: any) => {
			const workbook: XLSX.WorkBook = XLSX.read(e.target.result, { type: 'binary' })

			// Nome da primeira planilha no arquivo
			const firstSreadsheet: string = workbook.SheetNames[0]

			// Obter os dados da primeira planilha como array de objetos
			const data: Array<any> = XLSX.utils.sheet_to_json(workbook.Sheets[firstSreadsheet], { raw: false })

			// Instância um objeto do tipo passado pelo parametro model
			const objectModel = new model()

			// Array para armazenar informações sobre os erros
			const errors: Array<ImportInventoryError> = []

			// Verifica se todos os dados presentes na planilha contêm os atributos do objeto Modelo
			for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {

				const obj = data[rowIndex]

				const errorInfo: ImportInventoryError = {
					row: rowIndex + 1,
					error: []
				} // Objeto para armazenar informações sobre o erro, se houver.

				let isRowWithError = false

				for (const attr of Object.keys(objectModel)) {
					if (!obj.hasOwnProperty(attr)) {
						errorInfo.error.push(`Faltando o campo ${attr}`)
						isRowWithError = true
					}

					const attrType = typeof objectModel[attr]

					switch (attrType) {
						case 'number':

							if (obj[attr] !== undefined ) {
								const numberValue = parseFloat(obj[attr].replace(',', '.'))

								if (isNaN(numberValue)) {
									errorInfo.error.push(`Valor inválido para o campo ${attr}`)
									isRowWithError = true

									obj[attr] = null
								} else {

									obj[attr] = numberValue
								}
							}

							break

						case 'object':

							if (objectModel[attr] instanceof Date && typeof obj[attr] === 'string') {

								const dateValue = new Date(moment_(obj[attr]).format('YYYY-MM-DD 08:00:00.000'))

								if (isNaN(dateValue.getTime())) {
									errorInfo.error.push(`Valor inválido para o campo ${attr}`)
									isRowWithError = true

									obj[attr] = null
								} else {
									obj[attr] = dateValue
								}
							}

							break
					}
				}

				if (isRowWithError) {
					errors.push(errorInfo)
				}
			}

			const isValid = errors.length === 0

			if (isValid) {
				// Mapear os dados para o tipo T
				const typedData: Array<T> = data.map(obj => Object.assign(new model(), obj))

				subject.next(typedData)
				subject.complete()
			} else {
				subject.error(errors)
			}
		  }

		reader.readAsBinaryString(file)

		return subject.asObservable()
	}

	/**
	 * Valida se o arquivo é um Excel.
	 *
	 * @param {File} file - Árquivo a ser validado.
	 * @returns {boolean} - Retorna true se o arquivo for do tipo excel.
	 */
	private isValidXlsxFile(file: File): boolean {
		return file.name.endsWith('.xlsx')
	}

	private saveAsExcelFile(buffer: any, fileName: string): void {
		const data: Blob = new Blob([buffer], { type: EXCEL_TYPE })
		FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION)
	}
}
